/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * i18n setup. UI strings live in the DB (global per-language bundles) and are fetched from the API
 * (`/api/storefront/i18n/*`), so a storefront language can be added/edited/auto-translated without a
 * frontend rebuild. English is the only bundle compiled in — it's the instant first paint and the
 * offline fallback. Other languages load from the API (cached in localStorage, keyed by version) and
 * the language switcher is driven by the API manifest. If the API is unreachable we degrade to the
 * bundled English + a hardcoded fallback language list.
 */

import { useEffect, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

const STORAGE_KEY = 'mezw_lang';
const BUNDLE_CACHE_PREFIX = 'mezw_i18n_';
const API = '/api/storefront/i18n';

export interface StorefrontLanguage {
  code: string;
  label: string;
  name: string;
  nativeName: string;
  flag: string;
}

/**
 * Fallback language list — used before the API manifest loads and if it's unreachable. Mirrors the
 * languages seeded in the DB; the manifest (DB) is the source of truth once it loads.
 */
export const LANGUAGES: StorefrontLanguage[] = [
  { code: 'en', label: 'EN', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'ES', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'PT', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'fr', label: 'FR', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'DE', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'IT', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', label: 'RU', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'zh-TW', label: '繁中', name: 'Chinese (Traditional)', nativeName: '繁體中文', flag: '🇹🇼' },
  { code: 'zh-CN', label: '简中', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳' },
];

// ---- dynamic language list (driven by the API manifest, falls back to LANGUAGES) ----
let liveLanguages: StorefrontLanguage[] = LANGUAGES;
const langListeners = new Set<(l: StorefrontLanguage[]) => void>();
const manifestVersions = new Map<string, number>(); // code -> bundle version (for cache-busting)

export function getLanguages(): StorefrontLanguage[] {
  return liveLanguages;
}

/** Subscribe a component to the (DB-driven) language list so newly-added languages appear live. */
export function useStorefrontLanguages(): StorefrontLanguage[] {
  const [langs, setLangs] = useState<StorefrontLanguage[]>(liveLanguages);
  useEffect(() => {
    const fn = (l: StorefrontLanguage[]) => setLangs(l);
    langListeners.add(fn);
    setLangs(liveLanguages);
    return () => { langListeners.delete(fn); };
  }, []);
  return langs;
}

function setLiveLanguages(next: StorefrontLanguage[]) {
  liveLanguages = next;
  langListeners.forEach((fn) => fn(next));
}

function detectLanguage(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && liveLanguages.some((l) => l.code === saved)) return saved;
  const nav = (navigator.language || 'en').toLowerCase();
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('pt')) return 'pt';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('de')) return 'de';
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('ru')) return 'ru';
  // Traditional Chinese (Taiwan / Hong Kong / Hant) vs Simplified (Mainland / Singapore / Hans).
  if (nav.includes('zh-tw') || nav.includes('zh-hant') || nav.includes('zh-hk')) return 'zh-TW';
  if (nav.includes('zh-cn') || nav.includes('zh-hans') || nav.includes('zh-sg') || nav.startsWith('zh')) return 'zh-CN';
  return 'en';
}

// ---- bundle cache (localStorage) ----
type CachedBundle = { version: number; translation: Record<string, any> };

/**
 * A translation bundle is only usable if it's a real, populated object (has the `nav` section every
 * bundle carries). This rejects an empty `{}` or a malformed `{ valueKind: 1 }` blob that an earlier
 * server bug could produce — without this guard such a bundle would be cached/applied and the whole
 * language would silently render the English fallback.
 */
function isUsableBundle(t: any): boolean {
  return !!t && typeof t === 'object' && !!t.nav && typeof t.nav === 'object';
}

function readCache(code: string): CachedBundle | null {
  try {
    const raw = localStorage.getItem(BUNDLE_CACHE_PREFIX + code);
    if (!raw) return null;
    const b = JSON.parse(raw) as CachedBundle;
    return isUsableBundle(b?.translation) ? b : null; // ignore (and let caller refetch) stale junk
  } catch { return null; }
}
function writeCache(code: string, b: CachedBundle) {
  try { localStorage.setItem(BUNDLE_CACHE_PREFIX + code, JSON.stringify(b)); } catch { /* quota */ }
}

function addBundle(code: string, translation: Record<string, any>) {
  i18n.addResourceBundle(code, 'translation', translation, true, true);
}

/**
 * Ensure the i18next resources for `code` are loaded: use the cached bundle immediately, then fetch from
 * the API if it's missing or a newer version is known. `en` is always present (compiled in).
 */
async function ensureBundle(code: string): Promise<void> {
  if (code === 'en') return;
  const wantVersion = manifestVersions.get(code);
  const cached = readCache(code); // already null unless usable

  // Paint instantly from a usable cache, then decide whether to revalidate.
  if (cached && !i18n.hasResourceBundle(code, 'translation')) addBundle(code, cached.translation);

  // Only skip the network when we KNOW the cached copy is the wanted version. If the manifest hasn't
  // loaded yet (wantVersion == null) or the cache is missing/stale, we still fetch — otherwise a
  // language with no usable cache (or a stale one) would stay stuck on the English fallback.
  if (cached && wantVersion != null && cached.version === wantVersion) return;

  try {
    const r = await fetch(`${API}/${encodeURIComponent(code)}`);
    if (!r.ok) throw new Error(String(r.status));
    const body = await r.json();
    const res = body?.result;
    if (res?.translation && isUsableBundle(res.translation)) {
      addBundle(code, res.translation);
      writeCache(code, { version: res.version ?? 1, translation: res.translation });
    }
  } catch {
    // API unreachable — keep whatever usable cache we already painted, else English fallback.
  }
}

/** Fetch the DB language manifest → update the switcher list + known bundle versions. */
async function loadManifest(): Promise<void> {
  try {
    const r = await fetch(`${API}/manifest`);
    if (!r.ok) return;
    const body = await r.json();
    const rows = body?.result;
    if (!Array.isArray(rows) || rows.length === 0) return;
    const byFallback = new Map(LANGUAGES.map((l) => [l.code, l]));
    const next: StorefrontLanguage[] = rows.map((m: any) => ({
      code: m.code,
      name: m.englishName || byFallback.get(m.code)?.name || m.code,
      nativeName: m.nativeName || byFallback.get(m.code)?.nativeName || m.code,
      flag: m.flag || byFallback.get(m.code)?.flag || '🌐',
      label: byFallback.get(m.code)?.label || String(m.code).toUpperCase(),
    }));
    rows.forEach((m: any) => { if (m.code) manifestVersions.set(m.code, m.version ?? 1); });
    setLiveLanguages(next);
  } catch { /* keep fallback list */ }
}

// ---- boot ----
// Seed cached non-en bundles synchronously so a returning visitor renders instantly in their language.
const initialResources: Record<string, { translation: Record<string, any> }> = { en: { translation: en } };
const boot = detectLanguage();
if (boot !== 'en') {
  const c = readCache(boot);
  if (c) initialResources[boot] = { translation: c.translation };
}

i18n.use(initReactI18next).init({
  resources: initialResources,
  lng: boot,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Refresh from the DB: load the manifest, then resolve the wanted language (a saved language that the
// fallback list didn't know about — e.g. a newly-added one — is only recognized once the manifest loads).
void (async () => {
  await loadManifest();
  const saved = localStorage.getItem(STORAGE_KEY);
  const want = saved && liveLanguages.some((l) => l.code === saved) ? saved : (i18n.language || boot);
  await ensureBundle(want);
  if (want !== i18n.language) await i18n.changeLanguage(want);
  else i18n.emit('languageChanged', i18n.language); // re-render with freshly-loaded strings
})();

export async function setLanguage(code: string) {
  localStorage.setItem(STORAGE_KEY, code);
  await ensureBundle(code);
  await i18n.changeLanguage(code);
}

export default i18n;
